import { useEffect, useMemo, useRef } from 'react';
import type { Track } from '@pujolisique/shared';
import { useLyrics } from '../api/hooks';
import { usePlayer } from '../player/store';
import { Loading } from './States';

const SOURCES: Record<string, string> = {
  jellyfin: 'Jellyfin',
  navidrome: 'Navidrome',
  lrclib: 'LRCLIB',
};

export function Lyrics({ track }: { track: Track }) {
  const q = useLyrics(track.id);
  const time = usePlayer((s) => s.currentTime);
  const seek = usePlayer((s) => s.seek);
  const box = useRef<HTMLDivElement>(null);
  const userScrollAt = useRef(0);
  const lyrics = q.data?.lyrics;

  const active = useMemo(() => {
    if (!lyrics?.synced) return -1;
    const ms = time * 1000 + 250;
    let idx = -1;
    for (let i = 0; i < lyrics.lines.length; i++) {
      if ((lyrics.lines[i].time ?? 0) <= ms) idx = i;
      else break;
    }
    return idx;
  }, [lyrics, time]);

  useEffect(() => {
    if (active < 0 || !box.current || Date.now() - userScrollAt.current < 4000) return;
    const el = box.current.querySelector<HTMLElement>(`[data-i="${active}"]`);
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    box.current.scrollTo({ top: el.offsetTop - box.current.clientHeight / 2 + el.clientHeight / 2, behavior: reduce ? 'auto' : 'smooth' });
  }, [active]);

  if (q.isLoading) return <Loading label="Recherche des paroles" />;
  if (!lyrics || (!lyrics.lines.length && !lyrics.instrumental))
    return (
      <div className="lyrics lyrics--empty">
        <p className="state__title">Pas de paroles pour ce titre</p>
        <p className="state__text">Ni le serveur musical ni LRCLIB n’en proposent. Ajoutez un fichier .lrc à côté du morceau pour les afficher ici.</p>
      </div>
    );
  if (lyrics.instrumental)
    return (
      <div className="lyrics lyrics--empty">
        <p className="state__title">Morceau instrumental</p>
      </div>
    );

  return (
    <div
      className={`lyrics ${lyrics.synced ? 'lyrics--synced' : ''}`}
      ref={box}
      onWheel={() => (userScrollAt.current = Date.now())}
      onTouchMove={() => (userScrollAt.current = Date.now())}
    >
      {lyrics.lines.map((l, i) =>
        lyrics.synced ? (
          <button
            key={i}
            data-i={i}
            className={`lyrics__line ${i === active ? 'is-active' : i < active ? 'is-past' : ''} ${!l.text ? 'is-gap' : ''}`}
            onClick={() => {
              userScrollAt.current = 0;
              seek((l.time ?? 0) / 1000);
            }}
          >
            {l.text || '♪'}
          </button>
        ) : (
          <p key={i} className={`lyrics__line ${!l.text ? 'is-gap' : ''}`}>
            {l.text || ' '}
          </p>
        ),
      )}
      <p className="lyrics__source">Paroles : {SOURCES[lyrics.source] ?? lyrics.source}</p>
    </div>
  );
}
